import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PRIORITY_CONFIG, STATUS_CONFIG, PLAN_TYPE_ICON_CONFIG } from '../../config/agent.config';
import { StepRow } from './StepRow';
import { ValidateButton } from './ValidateButton';
import { agentAPI } from '../../api/agent.api';
import type { ActionPlan, StepContent, RefusalReason } from '../../types/agent.types';

interface PlanCardProps {
  plan: ActionPlan;
  onReviewStep: (planId: string, stepNumber: number, review_status: string, modified_content?: StepContent, refusal_reason?: RefusalReason) => Promise<void>;
  onValidatePlan: (planId: string, comment?: string) => Promise<void>;
  onRetryPlan: (planId: string) => Promise<void>;
  isValidating: boolean;
}

function timeAgo(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });

  if (isToday) return time;
  if (isYesterday) return `hier ${time}`;
  return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' }) + ' ' + time;
}

const PlanTypeIconSvg: React.FC<{ icon: string }> = ({ icon }) => {
  switch (icon) {
    case 'payment':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
        </svg>
      );
    case 'expert':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
        </svg>
      );
    case 'document':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
        </svg>
      );
    case 'confirmation':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'email':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" />
        </svg>
      );
    default:
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
        </svg>
      );
  }
};

function getPlanTypeIcon(planType: string): { icon: string; className: string } {
  return PLAN_TYPE_ICON_CONFIG[planType] || { icon: 'document', className: 'agent-card-icon--document' };
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  onReviewStep,
  onValidatePlan,
  onRetryPlan,
  isValidating,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [comment, setComment] = useState('');

  const handleOpenTracker = useCallback(() => {
    const scrollContainer = document.querySelector('.shared-main-content');
    if (scrollContainer) {
      sessionStorage.setItem('agentLeaScrollPosition', String(scrollContainer.scrollTop));
    }
    const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';
    navigate(`${basePath}/tracker/${plan.affaire_id}`, {
      state: { from: `${basePath}/agent-lea`, fromAgentLea: true },
    });
  }, [navigate, location.pathname, plan.affaire_id]);

  const priorityConfig = PRIORITY_CONFIG[plan.priority];
  const statusConfig = STATUS_CONFIG[plan.status];
  const snapshot = plan.affaire_snapshot || {};
  const isPending = plan.status === 'pending';
  const typeIcon = getPlanTypeIcon(plan.plan_type);

  const handleApprove = useCallback(
    (stepNumber: number) => onReviewStep(plan.id, stepNumber, 'approved'),
    [plan.id, onReviewStep]
  );

  const handleModify = useCallback(
    (stepNumber: number, content: StepContent) =>
      onReviewStep(plan.id, stepNumber, 'modified', content),
    [plan.id, onReviewStep]
  );

  const handleRefuse = useCallback(
    (stepNumber: number, reason: RefusalReason) =>
      onReviewStep(plan.id, stepNumber, 'refused', undefined, reason),
    [plan.id, onReviewStep]
  );

  const handleUndo = useCallback(
    (stepNumber: number) => onReviewStep(plan.id, stepNumber, 'pending'),
    [plan.id, onReviewStep]
  );

  const handleValidate = useCallback(
    () => onValidatePlan(plan.id, comment || undefined),
    [plan.id, comment, onValidatePlan]
  );

  // Count steps for execution info
  const isStepSkipped = (s: typeof plan.steps[0]) => {
    const d = (s.execution_result?.details || '').toLowerCase();
    return s.review_status === 'refused' || d.includes('refusé') || d.includes('refusée') || d.includes('skippé') || d.includes('ignorée') || d.includes('non exécuté');
  };
  const executedSuccessfully = plan.steps.filter(
    (s) => !isStepSkipped(s) && (s.review_status === 'approved' || s.review_status === 'modified' || (s.execution_result && s.execution_result.success))
  ).length;
  const skippedCount = plan.steps.filter((s) => isStepSkipped(s)).length;
  const failedSteps = plan.steps.filter(
    (s) => s.execution_result && !s.execution_result.success && !isStepSkipped(s)
  ).length;

  return (
    <div className="agent-plan-card">
      {/* Card Header */}
      <div className="agent-card-header">
        <div className={`agent-card-icon ${typeIcon.className}`}>
          <PlanTypeIconSvg icon={typeIcon.icon} />
        </div>

        <div className="agent-card-body">
          <div className="agent-card-title-row">
            <button
              className="agent-card-affaire"
              onClick={handleOpenTracker}
            >
              #{plan.affaire_id}
            </button>
            <span className="agent-card-separator">&middot;</span>
            <span className="agent-card-client">{snapshot.client_name || '\u2014'}</span>
          </div>
          {snapshot.address && (
            <div className="agent-card-address">{snapshot.address}</div>
          )}
          <div className="agent-card-tags">
            {plan.analysis_text?.startsWith('[OBSERVATION]') && (
              <span className="agent-card-tag agent-card-tag--observation">OBSERVATION</span>
            )}
            {snapshot.stage && (
              <span className="agent-card-tag agent-card-tag--stage">{snapshot.stage}</span>
            )}
            {snapshot.responsable && (
              <span className="agent-card-tag agent-card-tag--person">{snapshot.responsable}</span>
            )}
          </div>
        </div>

        <div className="agent-card-meta">
          {isPending ? (
            <span className={`agent-card-status-badge ${priorityConfig.badgeClass}`}>
              {priorityConfig.label}
            </span>
          ) : (
            <span className={`agent-card-status-badge ${statusConfig.badgeClass}`}>
              {statusConfig.label}
            </span>
          )}
          <div className="agent-card-timestamp">{timeAgo(plan.created_at)}</div>
        </div>
      </div>

      {/* Cancellation reason */}
      {plan.status === 'cancelled' && plan.cancellation_reason && (
        <div className="agent-cancel-reason">
          Raison: {plan.cancellation_reason}
        </div>
      )}

      {/* Summary + expandable detail (merged analysis + context) */}
      {isPending && plan.analysis_text && (
        <>
          <div className="agent-card-divider" />
          <PlanSummary plan={plan} showDetail={showAnalysis} onToggle={() => setShowAnalysis(!showAnalysis)} />
        </>
      )}

      {/* Steps */}
      {(plan.status !== 'cancelled' || plan.steps.length > 0) && (
        <>
          <div className="agent-card-divider" />
          <div className="agent-steps-label">
            Plan d'action &middot; {plan.steps.length} etape{plan.steps.length > 1 ? 's' : ''}
          </div>
          {plan.steps.map((step) => (
            <StepRow
              key={step.step_number}
              step={step}
              planStatus={plan.status}
              onApprove={handleApprove}
              onModify={handleModify}
              onRefuse={handleRefuse}
              onUndo={handleUndo}
              affaireRoles={{
                rv: snapshot.rv_person || undefined,
                in: snapshot.in_person || undefined,
                eval1: snapshot.eval1_person || undefined,
                eval2: snapshot.eval2_person || undefined,
                eval3: snapshot.eval3_person || undefined,
              }}
              affaireSnapshot={snapshot}
            />
          ))}
        </>
      )}

      {/* Execution info */}
      {(plan.status === 'validated' || plan.status === 'executing') && (
        <div className="agent-execution-info agent-execution-info--pending">
          <span className="agent-execution-spinner" />
          En attente d'execution par l'agent (prochain cycle: ~15 min)
        </div>
      )}

      {plan.status === 'completed' && (
        <div className={`agent-execution-info ${executedSuccessfully === 0 ? 'agent-execution-info--refused' : 'agent-execution-info--success'}`}>
          {executedSuccessfully > 0 ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {executedSuccessfully} action{executedSuccessfully > 1 ? 's' : ''} executee{executedSuccessfully > 1 ? 's' : ''} avec succes
              {skippedCount > 0 && `, ${skippedCount} ignoree${skippedCount > 1 ? 's' : ''}`}
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Plan refuse — {skippedCount} etape{skippedCount > 1 ? 's' : ''} refusee{skippedCount > 1 ? 's' : ''}
            </>
          )}
          {plan.executed_at && ` \u00B7 Execute ${timeAgo(plan.executed_at)}`}
        </div>
      )}

      {plan.status === 'failed' && (
        <div className="agent-execution-info agent-execution-info--error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          {failedSteps} action{failedSteps > 1 ? 's' : ''} echouee{failedSteps > 1 ? 's' : ''} sur {plan.steps.length}
          <button className="agent-retry-btn" onClick={() => onRetryPlan(plan.id)}>
            Reessayer
          </button>
        </div>
      )}

      {/* Card Footer — Comment + Validate (pending only) */}
      {isPending && (
        <div className="agent-card-footer">
          <div className="agent-comment-input">
            <textarea
              rows={1}
              placeholder="Ajouter un commentaire pour l'agent..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onFocus={(e) => { e.target.rows = 3; }}
              onBlur={(e) => { if (!e.target.value) e.target.rows = 1; }}
            />
          </div>
          <ValidateButton
            steps={plan.steps}
            isValidating={isValidating}
            onValidate={handleValidate}
          />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PlanSummary — One-line status + action, expandable detail
// ============================================================================

const PlanSummary: React.FC<{ plan: ActionPlan; showDetail: boolean; onToggle: () => void }> = ({ plan, showDetail, onToggle }) => {
  const planId = plan.id;
  const affaireId = plan.affaire_id;
  const situationType = plan.plan_type;
  const [context, setContext] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [contextLoaded, setContextLoaded] = useState(false);

  // Load context on first expand
  useEffect(() => {
    if (!showDetail || contextLoaded) return;
    setLoading(true);
    setContextLoaded(true);
    agentAPI.getDecisions({ affaire_id: affaireId, limit: 5 })
      .then(response => {
        const match = (response.data || []).find((d: any) =>
          d.plan_id === planId || (d.situation_type === situationType && d.outcome === 'plan_created')
        );
        setContext(match || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [showDetail, contextLoaded, planId, affaireId, situationType]);

  // Build one-line summary from analysis_text (first sentence) and recommendation
  const situationLabel = (situationType || '').replace(/_/g, ' ');
  const snapshot = plan.affaire_snapshot || {} as any;
  // Use Claude's summary if available, otherwise extract first sentence
  const analysisFirstLine = snapshot.summary || (() => {
    let text = plan.analysis_text || '';
    text = text.replace(/^\[OBSERVATION\][^\n]*\n?/i, '').trim();
    const match = text.match(/^(.{40,}?[.!])\s+(?=[A-ZÀ-Ö])/);
    return match ? match[1].trim() : text.slice(0, 200).trim();
  })();
  const recommendationLine = plan.recommendation || '';

  return (
    <div className="agent-summary-box">
      {/* Always visible: one-line status + action */}
      <div className="agent-summary-lines">
        <div className="agent-summary-line">
          <span className="agent-summary-label">Constat</span>
          <span className="agent-summary-value">{analysisFirstLine}</span>
        </div>
        {recommendationLine && (
          <div className="agent-summary-line">
            <span className="agent-summary-label">Action</span>
            <span className="agent-summary-value">{recommendationLine}</span>
          </div>
        )}
      </div>
      <button className="agent-summary-toggle" onClick={onToggle}>
        {showDetail ? 'Masquer detail' : 'Voir detail'}
      </button>

      {/* Expandable: full analysis + context + prompt */}
      {showDetail && (
        <div className="agent-summary-detail">
          {/* Situation + confidence */}
          <div className="agent-context-section">
            <div className="agent-context-label">Situation</div>
            <div className="agent-context-value">
              {situationLabel}
              {context?.confidence != null && <> · Confiance {Math.round(context.confidence * 100)}%</>}
            </div>
          </div>

          {/* Gate results */}
          {context?.gate_results && Object.keys(context.gate_results).length > 0 && (
            <div className="agent-context-section">
              <div className="agent-context-label">Conditions</div>
              <div className="agent-context-gates">
                {Object.entries(context.gate_results).map(([key, val]: [string, any]) => (
                  <span key={key} className={`agent-context-gate ${val?.passed === false || val?.blocked ? 'agent-context-gate--fail' : 'agent-context-gate--pass'}`}>
                    {key.replace(/_/g, ' ')} {val?.actual !== undefined ? `(${val.actual})` : ''}
                    {val?.passed === false || val?.blocked ? ' \u2715' : ' \u2713'}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Full analysis */}
          <div className="agent-context-section">
            <div className="agent-context-label">Analyse complete</div>
            <div className="agent-context-value" style={{ whiteSpace: 'pre-wrap' }}>{plan.analysis_text}</div>
          </div>

          {/* Reasoning from decision log (if different from analysis) */}
          {context?.reasoning && context.reasoning !== plan.analysis_text && (
            <div className="agent-context-section">
              <div className="agent-context-label">Raisonnement</div>
              <div className="agent-context-value" style={{ whiteSpace: 'pre-wrap' }}>{context.reasoning}</div>
            </div>
          )}

          {/* Cost/latency */}
          {(context?.cost_usd || context?.latency_ms) && (
            <div className="agent-context-meta">
              {context.cost_usd && <span>Cout: ${Number(context.cost_usd).toFixed(4)}</span>}
              {context.latency_ms && <span>Duree: {(context.latency_ms / 1000).toFixed(1)}s</span>}
            </div>
          )}

          {/* Full prompt */}
          {context?.full_prompt && (
            <FullPromptSection prompt={context.full_prompt} />
          )}

          {loading && <div style={{ color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>Chargement du contexte...</div>}
        </div>
      )}
    </div>
  );
};

const FullPromptSection: React.FC<{ prompt: string }> = ({ prompt }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="agent-context-section" style={{ marginTop: 12 }}>
      <div className="agent-context-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Prompt complet envoye a Claude ({(prompt.length / 1000).toFixed(1)}k caracteres)</span>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer', color: '#6b7280' }}
        >
          {expanded ? 'Masquer' : 'Afficher'}
        </button>
      </div>
      {expanded && (
        <pre className="agent-full-prompt">{prompt}</pre>
      )}
    </div>
  );
};
